import { Box, Fade, ThemeProvider, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { useTelegram, useTelegramTheme, useDesktopLayout } from "../../hooks";
import { getAccessToken, getStoredToken } from "../../services/login";
import { getUserInfo } from "../../services/me";
import { getBetaStatus } from "../../services/beta";
import { Navigation } from "./Navigation";
import { DesktopSidebar } from "./DesktopSidebar";
import { NavigationProvider } from "../../contexts/NavigationContext";
import { FeatureFlagsProvider } from "../../contexts/FeatureFlagsContext";
import { OnboardingScreen } from "../onboarding";
import { BetaGate } from "../beta";
import { SplashScreen } from "../shared/SplashScreen";
import { AppLockScreen } from "../lock";
import { WebAuthScreen } from "../web-auth";
import { IS_TELEGRAM, IS_WEB, v } from "../../utils/runtime";
import {
  isMobileWebApp,
  isBiometricEnrolled,
  isSessionUnlocked,
} from "../../utils";

export function RootLayout() {
  const theme = useTelegramTheme();
  const { tg } = useTelegram();
  const location = useLocation();
  const [authReady, setAuthReady] = useState<boolean>(!!getStoredToken());
  // Веб-режим без активной сессии → показываем экран входа вместо
  // основного UI. Проверяется только в браузере; в TG initData всегда
  // даёт нам токен и `needsWebLogin` остаётся false.
  const [needsWebLogin, setNeedsWebLogin] = useState<boolean>(false);

  useEffect(() => {
    // В веб-режиме `Telegram.WebApp` — заглушка, ready/expand/fullscreen
    // работают как no-op, но вызовы безопасны (см. useTelegram.ts).
    tg.ready();
    tg.expand();
    if (IS_TELEGRAM && isMobileWebApp) {
      try {
        tg.requestFullscreen();
      } catch (error) {
        console.warn("Fullscreen request not supported:", error);
      }
    }
  }, [tg]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // TG: `getAccessToken()` меняет initData на JWT. Web: пробует
      // тихо рефрешнуть по httpOnly cookie; если не вышло — возвращает
      // null, и мы показываем экран логина.
      //
      // В веб-режиме без токена показываем экран email-логина.
      // Раньше в dev-режиме гейт отключали, чтобы удобно было смотреть
      // вёрстку через ngrok из TG. Но локально в браузере без TG нужен
      // тот же логин — иначе все `/me/*` летят в 401 без экрана входа.
      const token = await getAccessToken();
      if (cancelled) return;
      if (!token && IS_WEB) {
        setNeedsWebLogin(true);
      }
      setAuthReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Гейт онбординга: пока `/me` возвращает `is_onboarded === false`,
  // основной UI и нижняя навигация скрыты и вместо `<Outlet />`
  // рендерится экран регистрации. Тем же кешем пользуются и остальные
  // страницы приложения — ключ `user-info` во всём проекте один.
  const {
    data: me,
    isLoading: meLoading,
    isError: meError,
    isFetched: meFetched,
  } = useQuery({
    queryFn: getUserInfo,
    queryKey: ["user-info"],
    // В веб-режиме без сессии — не дёргаем /me: сначала логин.
    enabled: authReady && !needsWebLogin,
    staleTime: 30_000,
    refetchOnMount: true,
  });

  // Закрытый бета-тест: если админ включил гейт и у юзера нет активации,
  // рендерим <BetaGate /> ДО онбординга. Запрос дёргаем один раз при
  // готовности авторизации; после успешного redeem'а inv. валидируем кеш
  // и `beta.enabled && !beta.redeemed` становится false — гейт исчезает.
  const {
    data: beta,
    isFetched: betaFetched,
    isError: betaError,
  } = useQuery({
    queryFn: getBetaStatus,
    queryKey: ["beta-status"],
    enabled: authReady && !needsWebLogin,
    staleTime: 60_000,
    refetchOnMount: true,
  });

  const needsBetaCode = Boolean(beta?.enabled && !beta?.redeemed);
  const needsOnboarding = Boolean(me && me.is_onboarded === false);
  const gateLoading = authReady && (meLoading || (!betaFetched && !betaError));

  // -----------------------------------------------------------------
  // Splash-screen: перехватываем эстафету у HTML-заглушки из index.html,
  // ведём прогресс по реальным этапам и плавно уходим когда всё готово.
  //   25%  — React смонтирован (JS/CSS/бандл загружены)
  //   60%  — auth готов (initData → JWT)
  //  100%  — /me загружен (или упал — не блокируем)
  // -----------------------------------------------------------------
  const [splashProgress, setSplashProgress] = useState<number>(25);
  const [splashVisible, setSplashVisible] = useState<boolean>(true);

  useEffect(() => {
    if (authReady) {
      setSplashProgress((p) => Math.max(p, 60));
    }
  }, [authReady]);

  useEffect(() => {
    // Готовы, когда либо получили /me, либо запрос упал (сеть/бэк
    // временно недоступен) — блокировать UI неудачным /me не хотим,
    // пусть пользователь хотя бы попадёт в приложение.
    // В веб-режиме без сессии /me не дёргается вовсе — доводим прогресс
    // сразу, чтобы сплэш ушёл и открылся экран логина.
    const meDone = needsWebLogin || meFetched || meError;
    if (authReady && meDone) {
      setSplashProgress(100);
      // Даём анимации бара доиграть до 100%, потом снимаем сплэш.
      const t = setTimeout(() => setSplashVisible(false), 500);
      return () => clearTimeout(t);
    }
  }, [authReady, meFetched, meError, needsWebLogin]);

  // -----------------------------------------------------------------
  // Lock-screen (Face ID / Touch ID / PIN):
  //   • показываем ТОЛЬКО когда юзер уже прошёл онбординг и включил
  //     биометрию (`zero_biometric_enrolled = 1`);
  //   • текущая сессия WebApp ещё не была разблокирована
  //     (`sessionStorage.zero_session_unlocked !== "1"`).
  //
  // Флаг «сессия разблокирована» очищается при закрытии WebApp — каждое
  // новое открытие требует биометрию/PIN заново.
  // -----------------------------------------------------------------
  const [locked, setLocked] = useState<boolean>(
    () => isBiometricEnrolled() && !isSessionUnlocked(),
  );
  const shouldLock =
    locked && Boolean(me?.is_onboarded) && !splashVisible && !meError;

  // -----------------------------------------------------------------
  // Desktop shell: только для web-версии на широком экране. В Telegram
  // Mini App всегда рисуем «мобильную» разметку (независимо от tdesktop/
  // macos клиента — там ширина ограничена самой обёрткой).
  //
  // На md-lg (900..1200) sidebar рендерится в компактном режиме
  // (только иконки + tooltip), на xl+ — с подписями.
  // -----------------------------------------------------------------
  const isDesktop = useDesktopLayout();
  const showSidebar =
    isDesktop && !needsWebLogin && !needsOnboarding && !needsBetaCode;
  const isXl = useMediaQuery(theme.breakpoints.up("lg"));
  const sidebarCompact = isDesktop && !isXl;

  // Метка на <body>, за которую цепляются MUI-override'ы для drawer'ов
  // и backdrop'ов (см. baseThemeParameters.ts). Ставим/снимаем при смене
  // раскладки, чтобы в Telegram Mini App / на мобиле поведение
  // осталось прежним.
  useEffect(() => {
    document.body.classList.toggle("zero-desktop-shell", isDesktop);
    return () => {
      document.body.classList.remove("zero-desktop-shell");
    };
  }, [isDesktop]);

  return (
    <ThemeProvider theme={theme}>
      <NavigationProvider>
        <FeatureFlagsProvider>
        {/* Splash сверху всего — покрывает и HTML-заглушку из index.html,
            и рендер приложения. После fade-out unmountOnExit его убирает
            и открывается основной интерфейс. */}
        <SplashScreen progress={splashProgress} visible={splashVisible} />

        {/* Lock-screen поверх приложения. Юзер разблокирует через
            Face ID / Touch ID или ручной PIN. `zero_session_unlocked`
            в sessionStorage запоминает разблокировку на текущую сессию. */}
        {shouldLock && <AppLockScreen onUnlock={() => setLocked(false)} />}

        <Box
          sx={{
            minHeight: "100vh",
            background: theme.palette.background.default,
            position: "relative",
          }}
        >
          {/* Глобальный фон-градиент для десктопной веб-версии. На всех
              страницах, кроме экрана логина, лежит на всём viewport как
              атмосферная подложка. На мобиле/в TG градиент рендерится
              локально внутри Home. */}
          {isDesktop && !needsWebLogin && (
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100vh",
                backgroundImage:
                  theme.palette.mode === "dark"
                    ? `url('${v("/dark-theme-gradient.jpg")}')`
                    : `url('${v("/light-theme-gradient.jpg")}')`,
                backgroundPosition: "top center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                opacity: 0.2,
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
          )}
          {/* <LevelProgressBarBlock /> */}
          <Fade in={!splashVisible} timeout={{ enter: 300, exit: 0 }}>
            {/* В веб-режиме без активной сессии сразу показываем экран
                email-логина — до неё нет ни onboarding'а, ни beta gate,
                ни navigation. После успешного логина WebAuthScreen сам
                делает hard-reload и мы попадаем в обычный флоу. */}
            {needsWebLogin ? (
              <Box sx={{ width: "100%" }}>
                <WebAuthScreen />
              </Box>
            ) : isDesktop ? (
              // ------------------- DESKTOP SHELL -------------------
              // Общий контейнер max-width 1200px, в котором sidebar
              // и контент — соседи. Всё вместе центрируется на широком
              // экране, sidebar «прилипает» на скролле через sticky.
              // z-index: 1 поднимает контент над глобальным фоном.
              <Box
                sx={{
                  width: "100%",
                  minHeight: "100vh",
                  display: "flex",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  px: { md: "20px", lg: "24px" },
                  py: "20px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 1200,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "24px",
                  }}
                >
                  {showSidebar && (
                    <DesktopSidebar compact={sidebarCompact} />
                  )}
                  <DesktopContentArea
                    isHomePage={location.pathname === "/"}
                  >
                    {gateLoading ? (
                      <Box sx={{ minHeight: 220, width: "100%" }} />
                    ) : needsBetaCode ? (
                      <BetaGate />
                    ) : needsOnboarding ? (
                      <OnboardingScreen />
                    ) : (
                      <Outlet />
                    )}
                  </DesktopContentArea>
                </Box>
              </Box>
            ) : (
              // ------------------- MOBILE / TG SHELL -------------------
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  padding: "15px 22px",
                  paddingTop: isMobileWebApp ? "100px" : "20px",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  {gateLoading ? (
                    <Box sx={{ minHeight: 220, width: "100%" }} />
                  ) : needsBetaCode ? (
                    <BetaGate />
                  ) : needsOnboarding ? (
                    <OnboardingScreen />
                  ) : (
                    <Outlet />
                  )}
                </Box>
                {/* Нижнюю навигацию скрываем на онбординге и бета-гейте:
                    до завершения регистрации / активации кода ходить
                    между вкладками нельзя. Если /me упал — отображаем
                    как обычно, чтобы не блокировать UI. */}
                {!needsOnboarding &&
                  !needsBetaCode &&
                  !gateLoading &&
                  !meError &&
                  authReady && <Navigation />}
                {meError && authReady && !needsBetaCode && <Navigation />}
              </Box>
            )}
          </Fade>
        </Box>
        </FeatureFlagsProvider>
      </NavigationProvider>
    </ThemeProvider>
  );
}

// -----------------------------------------------------------------
// Внутренний контейнер контента для десктоп-шелла.
//
// На Home (pathname === "/") — полная ширина, чтобы двухколоночный
// grid дышал. На остальных страницах (Profile, Exchange, History,
// Cards и т.д.) — max-width 675px, чтобы контент не расползался
// по широкому экрану.
// -----------------------------------------------------------------

function DesktopContentArea({
  isHomePage,
  children,
}: {
  isHomePage: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: isHomePage ? "stretch" : "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: isHomePage ? "none" : 675,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
